/** Small app-wide UI stores: dialogs, command menu, issue selection, preferences. */
import { useSyncExternalStore } from "react";
import type { Id, Issue } from "./types";

export function createStore<T>(initial: T) {
  let state = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => state,
    set(next: T | ((s: T) => T)) {
      state = typeof next === "function" ? (next as (s: T) => T)(state) : next;
      listeners.forEach((l) => l());
    },
    subscribe(cb: () => void) {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
  };
}

export function useStore<T>(store: ReturnType<typeof createStore<T>>): T {
  return useSyncExternalStore(store.subscribe, store.get);
}

// ---- create issue dialog ---------------------------------------------------------

export interface CreateIssueState {
  open: boolean;
  /** Field values to start from; `draftId` resumes a saved draft. */
  defaults: Partial<Issue> & { teamId?: Id; draftId?: Id };
}
export const createIssueStore = createStore<CreateIssueState>({ open: false, defaults: {} });
export const useCreateIssue = () => useStore(createIssueStore);
export function openCreateIssue(defaults: CreateIssueState["defaults"] = {}) {
  createIssueStore.set({ open: true, defaults });
}

// ---- command menu -----------------------------------------------------------------

export type CommandPage = "root" | "status" | "priority" | "assignee" | "labels" | "project" | "cycle" | "estimate" | "team" | "dueDate" | "parent";
export interface CommandState {
  open: boolean;
  page: CommandPage;
  issueIds: Id[];
}
export const commandStore = createStore<CommandState>({ open: false, page: "root", issueIds: [] });
export const useCommand = () => useStore(commandStore);
export function openCommand(page: CommandPage = "root", issueIds: Id[] = []) {
  commandStore.set({ open: true, page, issueIds });
}

// ---- issue selection (lists and boards) --------------------------------------

export interface Selection {
  ids: Set<Id>;
  focused: Id | null;
  anchor: Id | null;
}
export const selectionStore = createStore<Selection>({ ids: new Set(), focused: null, anchor: null });
export const useSelection = () => useStore(selectionStore);

export function toggleSelected(id: Id) {
  selectionStore.set((s) => {
    const ids = new Set(s.ids);
    if (ids.has(id)) ids.delete(id);
    else ids.add(id);
    return { ...s, ids, anchor: id, focused: id };
  });
}

export function selectRange(order: Id[], id: Id) {
  selectionStore.set((s) => {
    const a = s.anchor ? order.indexOf(s.anchor) : -1;
    const b = order.indexOf(id);
    if (a < 0 || b < 0) return { ...s, ids: new Set([...s.ids, id]), anchor: id, focused: id };
    const [from, to] = a < b ? [a, b] : [b, a];
    return { ...s, ids: new Set([...s.ids, ...order.slice(from, to + 1)]), focused: id };
  });
}

export function clearSelection() {
  selectionStore.set((s) => (s.ids.size === 0 ? s : { ...s, ids: new Set(), anchor: null }));
}

export function setFocused(id: Id | null) {
  selectionStore.set((s) => (s.focused === id ? s : { ...s, focused: id }));
}

/** The issues a keyboard action should apply to: the selection, else the focused one. */
export function targetIssueIds(): Id[] {
  const s = selectionStore.get();
  if (s.ids.size) return [...s.ids];
  return s.focused ? [s.focused] : [];
}

// ---- preferences ------------------------------------------------------------------------

export function readPref<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(`dayax:${key}`);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function writePref(key: string, value: unknown) {
  try {
    localStorage.setItem(`dayax:${key}`, JSON.stringify(value));
  } catch {
    /* storage unavailable */
  }
}

export type Theme = "system" | "light" | "dark";
export const themeStore = createStore<Theme>(readPref<Theme>("theme", "system"));
export const useTheme = () => useStore(themeStore);

export function applyTheme(theme: Theme) {
  const dark = theme === "dark" || (theme === "system" && matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.classList.toggle("dark", dark);
}

export function setTheme(theme: Theme, from?: { x: number; y: number }) {
  const apply = () => {
    writePref("theme", theme);
    themeStore.set(theme);
    applyTheme(theme);
  };
  const doc = document as Document & { startViewTransition?: (cb: () => void) => { finished: Promise<void> } };
  if (!from || !doc.startViewTransition || matchMedia("(prefers-reduced-motion: reduce)").matches) return apply();
  const root = document.documentElement;
  const r = Math.hypot(Math.max(from.x, innerWidth - from.x), Math.max(from.y, innerHeight - from.y));
  root.style.setProperty("--reveal-x", `${from.x}px`);
  root.style.setProperty("--reveal-y", `${from.y}px`);
  root.style.setProperty("--reveal-r", `${r}px`);
  root.dataset.themeSwitch = "";
  doc.startViewTransition(apply).finished.finally(() => delete root.dataset.themeSwitch);
}

/** Light or dark as currently shown, resolving "system". */
export function shownTheme(): "light" | "dark" {
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

export const sidebarStore = createStore<{ open: boolean; mobileOpen: boolean }>({ open: readPref("sidebar", true), mobileOpen: false });
export const useSidebar = () => useStore(sidebarStore);
export function toggleSidebar() {
  sidebarStore.set((s) => {
    writePref("sidebar", !s.open);
    return { ...s, open: !s.open };
  });
}
export function setMobileSidebar(open: boolean) {
  sidebarStore.set((s) => ({ ...s, mobileOpen: open }));
}

export const liveSessionStore = createStore<{ on: boolean; following: string | null }>({ on: false, following: null });
export const useLiveSession = () => useStore(liveSessionStore);

export const shortcutsStore = createStore(false);
export const useShortcutsOpen = () => useStore(shortcutsStore);
export const setShortcutsOpen = (open: boolean) => shortcutsStore.set(open);
